import { CompanyInfo, Currency, DealFlags, OrganizationContact, OrganizationContactFlags, OrganizationDeal, OrganizationFlags, OrganizationNote, OrganizationNoteFlags, partners, PaymentCycleTypes, PaymentListEntryStatus, PaymentListEntryStatuses, PaymentStatuses, PrimaryDealType, PrimaryDealTypes } from "../../../DTOs";
import { basicValidation } from "../basicValidation";



const contactValidation = {
    id: (value: string) => basicValidation.resourceId.isValid(value),
    name: (value: string) => basicValidation.validateString(value, false, false, 1, 100),
    email: (value: string) => basicValidation.validateString(value, false, false, 1, 100),
    phone: (value: string) => basicValidation.validateString(value, false, false, 0, 100),
    flag: (value: string) => typeof value === 'string' && OrganizationContactFlags.some(f => f === value),
    flags: (values: string[]) => typeof values === 'object' && Array.isArray(values) && values.every(v => contactValidation.flag(v)),
    note: (value: string) => basicValidation.validateString(value, false, false, 0, 5000),
    object:(value:OrganizationContact) => 
        value && typeof value === 'object' 
        && contactValidation.id(value.id)
        && contactValidation.name(value.name)
        && contactValidation.email(value.email) 
        && contactValidation.phone(value.phone)
        && contactValidation.flags(value.flags)
        && contactValidation.note(value.note),
    objectList:(values:OrganizationContact[]) => typeof values === 'object' && Array.isArray(values) && values.every(v => contactValidation.object(v))
};

type DealPaymentType = OrganizationDeal['payment'];
type PaymentListEntryType = DealPaymentType['payments'][number];

const dealPaymentListEntryValidation = {
    time: (value: Date) => value instanceof Date,
    amount: (value: number) => typeof value === 'number',
    currency: (value:Currency) => basicValidation.validateCurrency(value),
    status: (value:PaymentListEntryStatus) => 
        typeof value === 'string' 
        && PaymentListEntryStatuses.some(s => s === value),
    note : (value: string) => basicValidation.validateString(value, false, false, 0, 1000),
    object: (value: PaymentListEntryType) =>
        value && typeof value === 'object' 
        && dealPaymentListEntryValidation.time(value.time)
        && dealPaymentListEntryValidation.amount(value.amount)
        && dealPaymentListEntryValidation.currency(value.currency)
        && dealPaymentListEntryValidation.status(value.status)
        && dealPaymentListEntryValidation.note(value.note),
    objectList: (values: PaymentListEntryType[]) => typeof values === 'object' && Array.isArray(values) && values.every(v => dealPaymentListEntryValidation.object(v))
}

const dealPaymentValidation = {
    status: (value: string) => typeof value === 'string' && PaymentStatuses.some(s => s === value),
    cycle: (value: string) => typeof value === 'string' && PaymentCycleTypes.some(c => c === value),
    listPrice: (value: number) => typeof value === 'number',
    agreedPrice: (value: number) => typeof value === 'number',
    currency: (value: Currency) => basicValidation.validateCurrency(value),
    reminderDate: (value: Date | null) => value === null || value instanceof Date,
    payments: (values: PaymentListEntryType[]) => dealPaymentListEntryValidation.objectList(values),
    object: (value:DealPaymentType) =>
        value && typeof value === 'object'
        && dealPaymentValidation.status(value.status)
        && dealPaymentValidation.cycle(value.cycle)
        && dealPaymentValidation.listPrice(value.listPrice)
        && dealPaymentValidation.agreedPrice(value.agreedPrice)
        && dealPaymentValidation.currency(value.currency)
        && dealPaymentValidation.reminderDate(value.reminderDate)
        && dealPaymentValidation.payments(value.payments)
}

const dealValidation = {
    id: (value: string) => basicValidation.resourceId.isValid(value),
    title: (value: string) => basicValidation.validateString(value, false, false, 1, 100),
    start: (value: Date | null) => value === null || value instanceof Date,
    end: (value: Date | null) => value === null || value instanceof Date,
    dealType: (value: PrimaryDealType) =>
        typeof value === 'string' &&
        PrimaryDealTypes.some(f => f === value), 
    flags: (values: string[]) => basicValidation.validateFlags(DealFlags, values),
    payment: (value: DealPaymentType) =>
        value && typeof value === 'object'
        && dealPaymentValidation.object(value),
    object: (value: OrganizationDeal) =>
        value && typeof value === 'object'
        && dealValidation.id(value.id)
        && dealValidation.title(value.title)
        && dealValidation.start(value.start)
        && dealValidation.end(value.end)
        && dealValidation.dealType(value.dealType)
        && dealValidation.flags(value.flags)
        && dealValidation.payment(value.payment)
};

    const noteValidation = {
        id: (value: string) => basicValidation.resourceId.isValid(value),
        flags: (values: string[]) => typeof values === 'object' && Array.isArray(values) && values.every(value => OrganizationNoteFlags.some(flag => flag === value)),
        note: (value: string) => basicValidation.validateString(value, false, false, 1, 10000),
        deleted: (value: boolean) => typeof value === 'boolean',
        created: (value: Date) => value instanceof Date,
        updated: (value: Date | null) => value === null || value instanceof Date,
        createdByUserId: (value: string) => basicValidation.resourceId.isValid(value),
        object: (value: OrganizationNote) =>
        value && typeof value === 'object'
        && noteValidation.id(value.id)
        && noteValidation.flags(value.flags)
        && noteValidation.note(value.note)
        && noteValidation.deleted(value.deleted)
        && noteValidation.created(value.created)
        && noteValidation.updated(value.updated)
        && noteValidation.createdByUserId(value.createdByUserId)
    };

export const organizationFieldValidation = {
    organization: {
        id: (value: string) => basicValidation.resourceId.isValidOrEmpty(value),
        partner: (value: string | null) =>
        {
            if (value === null || value === '') return true;

            if (!basicValidation.validateString(value, false, false, 1, 100)) return false;
            if (!partners.some(p => p === value)) return false;

            return true;
        },
        companyInfo: (value: CompanyInfo) =>
        {
            if (!value) return false;
            if (!basicValidation.validateString(value.name, false, false, 1, 100)) return false;
            if (!basicValidation.validateString(value.address, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.altAddress, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.city, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.zip, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.phone, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.website, false, false, 0, 100)) return false;
            if (!basicValidation.validateString(value.note, false, false, 0, 5000)) return false;
            if(!basicValidation.validateFlags(OrganizationFlags, value.flags)) return false;
            return true;
        },
        contact: contactValidation,
        deal: dealValidation,
        note: noteValidation
    }
};